<template>
  <div>
    <v-dialog v-model="showing_modal" eager max-width="700">
      <v-card :disabled="loading" :loading="loading">
        <v-card-title class="headline">
          {{ $t("officer_config.config_curriculum") }}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <h2 class="mt-4 deep-orange--text">
            <span v-if="curriculum.LEVEL_ID == '1'">
              {{ objectTranslator(curriculum, "CURRICULUM_SHORT_NAME") }}
            </span>
            <span v-else>
              {{ objectTranslator(curriculum, "CURRICULUM_FULL_NAME") }}
            </span>
          </h2>

          <div
            v-for="(curriculum_constraint, index) in curriculum_constraints"
            :key="index"
          >
            <div class="mt-3">
              <v-btn
                class="mr-1"
                outlined
                x-small
                fab
                color="orange"
                :disabled="showing_add_edit"
                @click="open_edit_box(index, curriculum_constraint)"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>

              <v-btn
                class="mr-1"
                outlined
                x-small
                fab
                color="red"
                :disabled="showing_add_edit"
                @click="open_delete_modal(curriculum_constraint)"
              >
                <v-icon>mdi-trash-can</v-icon>
              </v-btn>

              <span style="font-size: 16px" v-show="editing_id !== index">
                {{
                  objectTranslator(
                    curriculum_constraint,
                    "CONSTRAINT_DESCRIPTION"
                  )
                }}
                : {{ curriculum_constraint.VALUE }}
              </span>

              <span
                style="font-size: 18px"
                class="blue--text"
                v-show="editing_id === index"
              >
                {{
                  objectTranslator(
                    curriculum_constraint,
                    "CONSTRAINT_DESCRIPTION"
                  )
                }}
                : {{ curriculum_constraint.VALUE }}
                <v-icon color="blue">mdi-pencil</v-icon>
              </span>
            </div>
          </div>

          <v-btn
            class="mt-5"
            small
            dark
            color="green"
            @click="open_add_box()"
            v-show="!showing_add_edit"
          >
            <v-icon>mdi-plus</v-icon>
            {{ $t("officer_config.addnew") }}
          </v-btn>

          <div class="add-box" v-show="showing_add_edit">
            <v-form
              ref="form"
              v-model="form_status"
              v-on:submit.prevent="add_form_validate"
            >
              <h2 class="deep-orange--text">
                {{
                  form_mode === FORM_MODES.ADD
                    ? $t("officer_config.addnew")
                    : $t("officer_config.edit")
                }}
              </h2>
              <v-row>
                <v-col cols="8" class="pb-0">
                  <v-autocomplete
                    v-model="constraint_id"
                    :label="$t('officer_config.constraint')"
                    :items="avialbleConstriants"
                    :item-value="(item) => item.CONSTRAINT_ID"
                    :item-text="
                      (item) => objectTranslator(item, 'CONSTRAINT_DESCRIPTION')
                    "
                    :rules="requiredRules"
                    outlined
                    :disabled="form_mode === FORM_MODES.EDIT"
                    dense
                  ></v-autocomplete>
                </v-col>
                <v-col cols="4" class="pb-0">
                  <v-text-field
                    v-model="constraint_value"
                    :label="$t('officer_config.constriant_value')"
                    outlined
                    dense
                    :rules="requiredRules"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-btn
                class="mt-0"
                small
                dark
                color="blue"
                @click="form_validate()"
              >
                <v-icon>mdi-content-save</v-icon>
                {{ $t("officer_config.save") }}
              </v-btn>
              <v-btn
                class="mt-0 ml-2"
                small
                dark
                color="orange"
                @click="close_box()"
              >
                <v-icon>mdi-close</v-icon>
                {{ $t("officer_config.cancel") }}
              </v-btn>
            </v-form>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" dark @click="showing_modal = false"
            >{{ $t("officer_config.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <ConfigDeleteDialog
      v-model="showing_delete_modal"
      :title="$t('officer_config.constraint')"
      :text="
        to_delete_constraint_item
          ? ' ' +
            objectTranslator(
              to_delete_constraint_item,
              'CONSTRAINT_DESCRIPTION'
            )
          : ''
      "
      :deleteFunction="delete_config_constraint"
    />
  </div>
</template>

<script>
import { createHelpers } from "vuex-map-fields";
import { objectTranslator } from "@/helper/translator";
import { getYearsModified } from "@/helper/dateTime";
import validator from "@/helper/validator";
import { listFilter } from "@/helper/filter";
import ConfigProvider from "@/services/config.provider";
import ConfigDeleteDialog from "@/components/config/ConfigDeleteDialog";

const configService = new ConfigProvider();

const { mapFields: mapOfficerConfigFields } = createHelpers({
  getterType: "officerConfig/getField",
  mutationType: "officerConfig/updateField",
});

const FORM_MODES = { ADD: 0, EDIT: 1 };

export default {
  name: "ConfigCurriculumConstraintAddEditDialog",
  props: {
    toggle_loading: Function,
    fetch_data: Function,
  },
  data: function () {
    return {
      loading: false,
      FORM_MODES,
      form_mode: null,
      form_status: null,
      showing_modal: false,
      showing_add_edit: false,
      editing_id: null,

      curriculum: [],
      curriculum_constraints: [],
      constraints: [],

      constraint_id: null,
      constraint_value: null,

      to_delete_constraint_item: null,
      showing_delete_modal: false,

      requiredRules: [validator.required],
    };
  },
  components: { ConfigDeleteDialog },
  computed: {
    levels() {
      return this.$store.state.ref.levels;
    },
    ...mapOfficerConfigFields({
      config_years: "config_years",
    }),
    avialbleConstriants() {
      if (this.form_mode === FORM_MODES.EDIT) return this.constraints;
      const used_id = this.curriculum_constraints.map(
        (item) => item.CONSTRAINT_ID
      );
      return this.constraints.filter(
        (item) => !used_id.includes(item.CONSTRAINT_ID)
      );
    },
  },
  methods: {
    listFilter,
    objectTranslator,
    getYearsModified,

    async fetch_curriculum_constraints(curriculum_id) {
      this.toggle_loading(true);
      const curriculum_constraints =
        await configService.getFilteredCurriculumConstraintsByCurriculum({
          curriculum_id,
        });
      this.curriculum_constraints = curriculum_constraints;

      if (this.constraints.length === 0) {
        const constraints = await configService.getConstraints();
        this.constraints = constraints;
      }
      this.toggle_loading(false);
    },

    // async fetch_faculty_curriculum(level_id) {
    //   this.toggle_loading(true);
    //   if (this.registrar_faculties.length == 0) {
    //     const faculties = await facultyService.getFaculties();
    //     this.registrar_faculties = faculties;
    //     const curriculums = await facultyService.getCurriculums(level_id);
    //     this.registrar_curriculums = curriculums;
    //   }
    //   this.toggle_loading(false);
    // },

    // async open_add_modal({ rounds, projects, year_id, level_id }) {
    //   await this.fetch_faculty_curriculum(level_id);
    //   this.config_year_id = year_id;
    //   this.config_level_id = level_id;
    //   this.rounds = rounds;
    //   this.projects = projects;
    //   this.form_mode = FORM_MODES.ADD;
    //   this.$refs.form.reset();
    //   this.showing_modal = true;
    // },

    open_add_box() {
      this.constraint_id = null;
      this.constraint_value = null;
      this.$refs.form.reset();
      this.form_mode = FORM_MODES.ADD;
      this.showing_add_edit = true;
    },
    open_edit_box(id, curriculum_constraint) {
      this.editing_id = id;
      this.constraint_id = curriculum_constraint.CONSTRAINT_ID;
      this.constraint_value = curriculum_constraint.VALUE;
      this.form_mode = FORM_MODES.EDIT;
      this.showing_add_edit = true;
    },
    close_box() {
      this.editing_id = null;
      this.showing_add_edit = false;
    },

    async open_edit_modal({ item }) {
      this.curriculum = item;
      this.fetch_curriculum_constraints(item.CURRICULUM_ID);
      this.close_box();
      this.showing_modal = true;
    },

    open_delete_modal(item) {
      this.to_delete_constraint_item = item;
      this.showing_delete_modal = true;
    },
    async delete_config_constraint() {
      this.loading = true;
      let response = await configService
        .deleteCurriculumConstraint({
          CURRICULUM_ID: this.to_delete_constraint_item.CURRICULUM_ID,
          CONSTRAINT_ID: this.to_delete_constraint_item.CONSTRAINT_ID,
        })
        .catch((error) => {
          this.$toasted.error(JSON.stringify(error.response.data.message), {
            duration: 0,
          });
          this.loading = false;
        });

      this.fetch_curriculum_constraints(
        this.to_delete_constraint_item.CURRICULUM_ID
      );
      this.fetch_data(
        this.to_delete_constraint_item.LEVEL_ID,
        this.to_delete_constraint_item.YEAR_ID
      );

      if (response && response.status)
        this.$toasted.success(this.$t("toasted.delete_successfully"));
      else this.$toasted.error(this.$t("toasted.cannot_delete"));

      this.close_box();
      this.showing_delete_modal = false;
      this.loading = false;
    },

    form_validate() {
      this.save_form_validate();
    },

    save_form_validate: async function () {
      this.$refs.form.validate();
      if (this.form_status) {
        this.loading = true;
        let response = await configService
          .saveCurriculumConstraint({
            CURRICULUM_ID: this.curriculum.CURRICULUM_ID,
            CONSTRAINT_ID: this.constraint_id,
            VALUE: this.constraint_value,
          })
          .catch((error) => {
            this.$toasted.error(JSON.stringify(error.response.data.message), {
              duration: 0,
            });
            this.loading = false;
          });
        this.fetch_curriculum_constraints(this.curriculum.CURRICULUM_ID);
        this.fetch_data(this.curriculum.LEVEL_ID, this.curriculum.YEAR_ID);
        if (response && response.status)
          this.$toasted.success(this.$t("toasted.create_successfully"));
        else this.$toasted.error(this.$t("toasted.cannot_create"));
        this.close_box();
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.add-box {
  border-width: 1px;
  border-radius: 10px;
  border-style: solid;
  border-color: #333;
  padding: 15px;
  margin-top: 10px;
}
</style>
